import { readErrorMessage } from '@Components/util/utils'
import { CustomerModulesServiceAPI } from '@API/data-services/customer-modules-service'
import { RootState } from '@AppState/redux-store'
import { AuthScopes, CustomerModuleView, FeatureFlag } from '@Generated/back-office-client/src'
import {
  Badge,
  Card,
  Container,
  Flex,
  Heading,
  Section,
  Skeleton,
  Switch,
  Text,
} from '@radix-ui/themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { DisableConfirm } from './disable-confirm'

export const CustomerModules = () => {
  const queryClient = useQueryClient()
  const { activeCustomerId, currentUser } = useSelector((state: RootState) => state.users)
  const customerName =
    currentUser?.customers.find((c) => c.customerId === activeCustomerId)?.name ?? 'this customer'
  const canWrite = !!currentUser?.scopes.includes(AuthScopes.writecustomer_modules)
  const queryKey = [activeCustomerId, 'customer-modules']

  const [pendingDisable, setPendingDisable] = useState<CustomerModuleView | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => CustomerModulesServiceAPI.list(),
  })

  const mutation = useMutation({
    mutationFn: ({
      moduleId,
      enabled,
      features,
    }: {
      moduleId: string
      enabled: boolean
      features: FeatureFlag[]
    }) => CustomerModulesServiceAPI.setModule(moduleId, enabled, features),
    onSuccess: () => {
      setSubmitError(null)
      queryClient.invalidateQueries({ queryKey })
    },
    onError: async (error: unknown) => {
      setSubmitError(await readErrorMessage(error, 'Failed to update module'))
    },
  })

  const toggleModule = (mod: CustomerModuleView, enabled: boolean) => {
    if (!enabled) {
      setPendingDisable(mod)
      return
    }
    mutation.mutate({ moduleId: mod.moduleId, enabled: true, features: [] })
  }

  const toggleFeature = (mod: CustomerModuleView, feature: FeatureFlag, on: boolean) => {
    const features = on
      ? [...mod.enabledFeatures, feature]
      : mod.enabledFeatures.filter((f) => f !== feature)
    mutation.mutate({ moduleId: mod.moduleId, enabled: true, features })
  }

  const modules = data?.items ?? []

  return (
    <Container>
      <Section>
        <Flex direction="column" gap="4">
          <Heading as="h1" size="5">
            Customer Modules
          </Heading>
          {submitError ? <Text color="red">{submitError}</Text> : null}
          <Skeleton loading={isLoading}>
            <Flex direction="column" gap="3">
              {modules.map((mod) => (
                <Card key={mod.moduleId}>
                  <Flex direction="column" gap="3">
                    <Flex justify="between" align="center">
                      <Flex gap="2" align="center">
                        <Heading as="h2" size="3">
                          {mod.name}
                        </Heading>
                        {mod.gambytAdminOnly ? (
                          <Badge color="purple" variant="soft">
                            Gambyt only
                          </Badge>
                        ) : null}
                      </Flex>
                      <Flex gap="2" align="center">
                        <Switch
                          checked={mod.enabled}
                          disabled={!canWrite || mutation.isPending}
                          onCheckedChange={(checked) => toggleModule(mod, checked === true)}
                        />
                        <Text size="2">Enabled</Text>
                      </Flex>
                    </Flex>
                    {mod.enabled && mod.offeredFeatures.length > 0 ? (
                      <Flex direction="column" gap="2" pl="4">
                        {mod.offeredFeatures.map((feature) => (
                          <Flex key={feature} gap="2" align="center">
                            <Switch
                              size="1"
                              checked={mod.enabledFeatures.includes(feature)}
                              disabled={!canWrite || mutation.isPending}
                              onCheckedChange={(checked) =>
                                toggleFeature(mod, feature, checked === true)
                              }
                            />
                            <Text size="2">{feature}</Text>
                          </Flex>
                        ))}
                      </Flex>
                    ) : null}
                  </Flex>
                </Card>
              ))}
            </Flex>
          </Skeleton>
        </Flex>
      </Section>
      <DisableConfirm
        target={pendingDisable}
        customerName={customerName}
        onClose={() => setPendingDisable(null)}
        onConfirm={(mod) => {
          mutation.mutate({ moduleId: mod.moduleId, enabled: false, features: [] })
          setPendingDisable(null)
        }}
      />
    </Container>
  )
}
